'use client';

import * as React from 'react';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverEventsQuery } from '@loci/api';

import { Switch } from '@/components/atoms/primitives/switch';
import { cn } from '@/components/atoms/primitives/utils';
import { useCityContext } from '@/lib/city-context.client';
import { defaultCityCode } from '@/lib/cross-host-urls';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';

/**
 * @fileoverview `DiscoverFollowToggle` — the single "Only who I follow" switch
 * for the `/discover` filter rail and mobile sheet (WEB-662 / DPL-12).
 *
 * Replaces TWO rails: `DiscoverFollowedArtistsRail` (FRE-913, an avatar list
 * that was purely informational — it never filtered anything) and
 * `DiscoverFollowedVenuesRail` (FRE-914/FRE-1145, a checkbox list that pushed
 * ids into `?venue=`). Together they consumed a large share of the rail to
 * express what most viewers want as one yes/no: *just show me the people and
 * places I follow.*
 *
 * **Why one switch is more honest than two lists.** The venues rail asked the
 * viewer to assemble the filter themselves out of client-held ids, and the
 * artists rail could not filter at all — there was no artist-id filter branch
 * in the where-builder. API-1664 replaced both with a server-resolved
 * follow-graph filter that narrows the events list AND the facet counts to
 * "venue is followed OR lineup includes a followed artist". The client now
 * asserts intent (`followedOnly=true`), not identity — no ids cross the wire.
 *
 * **Honest absence is preserved, not reintroduced as an empty control.** Both
 * retired rails already self-rendered `null` for anonymous viewers and for
 * viewers who follow nobody. The merged toggle keeps exactly that contract: a
 * follow filter offered to someone with an empty follow graph is an invitation
 * to a guaranteed dead end, so the control is hidden rather than shown
 * disabled. This differs deliberately from the WEB-659 / WEB-730 zero-count
 * treatment (which disables rather than hides): there, the facet is a real,
 * permanent dimension of the catalogue whose count happens to be zero under the
 * current filters; here, the entire premise of the control is absent for this
 * viewer.
 *
 * **Counts come from the shared subscription, never a new request.** The
 * envelope's uncapped `followedArtistsTotal` / `followedVenuesTotal`
 * (API-1667) ride on the same `/events/discover` payload the rest of Discover
 * already consumes via the canonical `buildDiscoverQueryArgs` cache key
 * (`useDiscoverEventArgs`). Structurally identical args dedupe into the single
 * cold-load request, so the cold-load single-fetch invariant is untouched.
 */

/** Switch label. Also the accessible name of the `switch` role. */
export const FOLLOW_TOGGLE_LABEL = 'Only who I follow';

/** Coerce a drifted/missing envelope total to zero rather than throwing. */
function readFollowTotal(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}

/**
 * Format the counts line — e.g. `12 ARTISTS · 8 VENUES`.
 *
 * Counts are the envelope's uncapped `followedArtistsTotal` /
 * `followedVenuesTotal` (API-1667 / WEB-742). Those totals describe exactly
 * the set `followedOnly` filters on, so the label and the filter are the same
 * number — no page-size constant, no `+` floor. A viewer following 30 artists
 * reads `30 ARTISTS` even when the display page is still 12 rows.
 *
 * A zero side is omitted entirely rather than rendered as `0 VENUES` — that
 * would advertise a dimension this viewer's follow graph does not contain.
 * Singular/plural is respected so `1 ARTIST` never reads as a typo. Returns
 * `null` when both counts are zero — a state the View never renders in (see
 * {@link DiscoverFollowToggleView}), so that `null` is a guard, not a layout.
 */
export function formatFollowCounts(artistCount: number, venueCount: number): string | null {
  const parts: string[] = [];
  if (artistCount > 0) {
    parts.push(`${artistCount} ARTIST${artistCount === 1 ? '' : 'S'}`);
  }
  if (venueCount > 0) {
    parts.push(`${venueCount} VENUE${venueCount === 1 ? '' : 'S'}`);
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

export interface DiscoverFollowToggleViewProps {
  /** Whether the filter is currently on (`followedOnly=true` in the URL). */
  readonly checked: boolean;
  /** Called with the next on/off state when the switch is activated. */
  readonly onCheckedChange: (next: boolean) => void;
  /** Number of artists this viewer follows. Drives the counts line. */
  readonly artistCount: number;
  /** Number of venues this viewer follows. Drives the counts line. */
  readonly venueCount: number;
  /**
   * Unique id for the switch, used to associate the visible label via
   * `htmlFor`. The rail and the mobile drawer can be mounted concurrently, so
   * each host passes a distinct id rather than sharing the default.
   */
  readonly id?: string;
  /** Wrapper class names. */
  readonly className?: string;
}

/**
 * Pure presentational view — label + counts line on the left, switch on the
 * right.
 *
 * Renders `null` when the viewer follows nobody (`artistCount + venueCount ===
 * 0`), so the honest-absence contract holds even when the View is driven
 * directly by a story or a test rather than by the connected wrapper.
 *
 * Accessibility / touch:
 *   - Radix `Switch` renders a real `role="switch"` with `aria-checked`, is
 *     Tab-reachable, and toggles on **Space** (and Enter) with no extra wiring.
 *   - The visible label is bound with `htmlFor`/`id`, so it is both the
 *     accessible name and a second, larger activation target.
 *   - Geometry (WEB-1076 / FRE-1379): the row takes 12px vertical padding and
 *     the switch paints at 38×22 with a 16px thumb. The size is scoped here
 *     through `className`; the shared primitive keeps its fleet-aligned 42×24
 *     `.acc-switch` default (FRE-1164) for every other toggle.
 *   - Touch floor: `.a-hit-floor` grows the switch's hit box to 44×44 on
 *     coarse pointers only (atoms.css), so paint never grows for touch.
 */
export function DiscoverFollowToggleView({
  checked,
  onCheckedChange,
  artistCount,
  venueCount,
  id = 'discover-follow-toggle-switch',
  className,
}: DiscoverFollowToggleViewProps): React.ReactElement | null {
  const counts = formatFollowCounts(artistCount, venueCount);

  // Honest absence: a follow filter for a viewer who follows nobody can only
  // ever return an empty list. Render nothing at all.
  if (counts === null) return null;

  const countsId = `${id}-counts`;

  return (
    <div
      data-testid="discover-follow-toggle"
      className={cn('grid grid-cols-[1fr_auto] items-center gap-4 py-3', className)}
    >
      <div className="min-w-0">
        <label
          htmlFor={id}
          className="block cursor-pointer font-display text-sm font-medium leading-tight text-fg-1"
        >
          {FOLLOW_TOGGLE_LABEL}
        </label>
        <p
          id={countsId}
          data-testid="discover-follow-toggle-counts"
          className="mb-0 mt-1 font-mono text-micro uppercase leading-none tracking-[0.04em] text-fg-3"
        >
          {counts}
        </p>
      </div>
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        aria-describedby={countsId}
        data-testid="discover-follow-toggle-switch"
        // WEB-1076: 38×22 pill, 16px thumb inset 2px (on: 38 − 2 border − 16
        // − 2 = 18px travel). `.a-hit-floor` is the coarse-pointer 44×44 hit
        // box; its `::before` is a child of the button, so a press anywhere
        // inside it activates the switch.
        className="a-hit-floor h-5.5 w-9.5 [&>span]:size-4 [&>span[data-state=checked]]:translate-x-4.5"
      />
    </div>
  );
}

export interface DiscoverFollowToggleProps {
  /**
   * Active city — drives the shared subscription's geo anchor. Optional:
   * defaults to the city context (falling back to the registry default), so a
   * host can mount `<DiscoverFollowToggle />` with no props.
   */
  readonly cityCode?: CityCode;
  /**
   * Unique id for the switch. The rail and the mobile drawer both mount this
   * molecule, so each host passes its own id to keep `htmlFor` associations
   * unambiguous.
   */
  readonly id?: string;
  /** Wrapper class names forwarded to the View. */
  readonly className?: string;
}

/**
 * Connected "Only who I follow" toggle. Reads the SHARED discover subscription
 * via the canonical `buildDiscoverQueryArgs` cache key, so it never issues a
 * request of its own (cold-load single-fetch invariant), and reads/writes the
 * `followedOnly` URL param via `useDiscoverParams`.
 *
 * Renders `null` unless the authenticated envelope reports at least one follow:
 *   - anonymous → no `authenticatedEnvelope` → `null`
 *   - signed in but follows nobody → both totals zero → `null`
 *   - signed in with any follow → render the toggle
 *
 * Writing `false` (rather than `undefined`) when toggling off is deliberate and
 * safe: `serializeDiscoverParams` and `buildDiscoverQueryArgs` both treat
 * anything other than `true` as absence, so the URL key and the wire param are
 * dropped and the resulting cache key matches a never-toggled cold load exactly.
 */
export function DiscoverFollowToggle({
  cityCode,
  id,
  className,
}: DiscoverFollowToggleProps): React.ReactElement | null {
  const city = useCityContext();
  const resolvedCityCode = cityCode ?? city?.code ?? defaultCityCode();

  const { params, setParams } = useDiscoverParams();
  const queryArgs = useDiscoverEventArgs(params, resolvedCityCode);
  const { data } = useDiscoverEventsQuery(queryArgs);

  const envelope = data?.authenticatedEnvelope;
  // Honest absence keys off the UNCAPPED totals (API-1667 / WEB-742), never
  // `followedArtists.length` — those arrays are a `take:`-capped display page
  // (12 / 8) and would hide the toggle for a viewer whose page failed while
  // still following people, or under-count a viewer past the cap. A drifted
  // non-number total is treated as zero so a bad payload can't throw and
  // blank the rail.
  const artistCount = readFollowTotal(envelope?.followedArtistsTotal);
  const venueCount = readFollowTotal(envelope?.followedVenuesTotal);

  const handleCheckedChange = React.useCallback(
    (next: boolean) => {
      setParams({ followedOnly: next });
    },
    [setParams],
  );

  if (artistCount === 0 && venueCount === 0) return null;

  return (
    <DiscoverFollowToggleView
      id={id}
      checked={params.followedOnly === true}
      onCheckedChange={handleCheckedChange}
      artistCount={artistCount}
      venueCount={venueCount}
      className={className}
    />
  );
}
